/**
 * `AnalysisStrip` — universal scene-wide analysis tools.
 *
 * Lives at the top-left of the 3D canvas and is the single, project-wide
 * surface for tools that operate across every layer family:
 *
 *   - Camera view presets (Top / Front / Back / Right / Left / Iso / Fit)
 *   - Section plane (axis + offset)
 *   - Clipping box (enable / disable)
 *   - Render mode pivot (Shaded / Greyscale / X-Ray / Wireframe)
 *
 * Per the platform architecture plan (three tool tiers — universal,
 * group, per-layer), this strip owns Tier 1. Per-layer tools stay in
 * the right-rail `<ToolCardDock />` and the floating point-cloud /
 * 3D-model controls panels.
 *
 * The strip never imports `three` or `@giro3d/giro3d`. It delegates
 * every mutation to a narrow `AnalysisStripAdapter` so it can be unit
 * tested without an engine, and so future Cesium / Babylon adapters
 * satisfy the same surface without code changes here.
 */

import { useCallback, useState, type CSSProperties } from 'react';
import { Icon, IconButton, Stack, Text, tokens } from '@/design';
import type { CameraPreset } from '@/engines/giro3d/cameraPresets';
import type {
  ClippingBoxSpec,
  SectionPlaneSpec,
} from '@/engines/giro3d/clipController';

export type RenderMode = 'default' | 'greyscale' | 'xray' | 'wireframe';
export type SectionAxis = 'x' | 'y' | 'z';

/** Narrow adapter the strip needs from the active engine. */
export interface AnalysisStripAdapter {
  setCameraPreset?: (preset: CameraPreset) => boolean;
  fitToModel?: () => boolean;
  setSectionPlane?: (spec: SectionPlaneSpec | null) => boolean;
  setClippingBox?: (box: ClippingBoxSpec | null) => boolean;
  setRenderMode?: (mode: RenderMode) => void;
  getRenderMode?: () => RenderMode;
}

export interface AnalysisStripProps {
  adapter: AnalysisStripAdapter | null;
  /** Disable presets when no model is loaded yet (no bounds). */
  presetsDisabled?: boolean;
  'data-testid'?: string;
}

const CAMERA_PRESETS: ReadonlyArray<{
  preset: CameraPreset;
  label: string;
  icon: 'photo_camera' | 'home' | '360' | 'crop_free';
}> = [
  { preset: 'top', label: 'Top', icon: 'photo_camera' },
  { preset: 'front', label: 'Front', icon: 'photo_camera' },
  { preset: 'right', label: 'Right', icon: 'photo_camera' },
  { preset: 'iso', label: 'Isometric', icon: '360' },
  { preset: 'fit', label: 'Fit to model', icon: 'home' },
];

const SECTION_AXES: ReadonlyArray<{
  axis: SectionAxis;
  label: string;
}> = [
  { axis: 'x', label: 'X' },
  { axis: 'y', label: 'Y' },
  { axis: 'z', label: 'Z' },
];

const RENDER_MODES: ReadonlyArray<{
  mode: RenderMode;
  label: string;
  icon: 'palette' | 'view_quilt' | 'view_compact' | 'texture';
}> = [
  { mode: 'default', label: 'Shaded', icon: 'view_quilt' },
  { mode: 'greyscale', label: 'Greyscale', icon: 'palette' },
  { mode: 'xray', label: 'X-Ray', icon: 'view_compact' },
  { mode: 'wireframe', label: 'Wireframe', icon: 'texture' },
];

export function AnalysisStrip({
  adapter,
  presetsDisabled = false,
  'data-testid': testId,
}: AnalysisStripProps) {
  const [sectionAxis, setSectionAxis] = useState<SectionAxis | null>(null);
  const [sectionPosition, setSectionPosition] = useState(0.5);
  const [clipEnabled, setClipEnabled] = useState(false);
  const [clipCenter, setClipCenter] = useState<readonly [number, number, number]>([0.5, 0.5, 0.5]);
  const [clipSize, setClipSize] = useState(0.8);
  const [renderMode, setRenderModeState] = useState<RenderMode>(
    () => adapter?.getRenderMode?.() ?? 'default',
  );
  const [presetsOpen, setPresetsOpen] = useState(false);

  const applyPreset = useCallback(
    (preset: CameraPreset) => {
      if (!adapter) return;
      if (preset === 'fit') adapter.fitToModel?.();
      else adapter.setCameraPreset?.(preset);
      setPresetsOpen(false);
    },
    [adapter],
  );

  const applySection = useCallback(
    (axis: SectionAxis | null, position: number) => {
      if (!adapter?.setSectionPlane) return;
      if (axis === null) {
        adapter.setSectionPlane(null);
        return;
      }
      adapter.setSectionPlane({
        axis,
        position,
        flip: false,
      });
    },
    [adapter],
  );

  const toggleSectionAxis = useCallback(
    (axis: SectionAxis) => {
      const next = sectionAxis === axis ? null : axis;
      setSectionAxis(next);
      applySection(next, sectionPosition);
    },
    [applySection, sectionAxis, sectionPosition],
  );

  const updateSectionPosition = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(1, next));
      setSectionPosition(clamped);
      if (sectionAxis) applySection(sectionAxis, clamped);
    },
    [applySection, sectionAxis],
  );

  const toggleClipBox = useCallback(() => {
    if (!adapter?.setClippingBox) return;
    const next = !clipEnabled;
    setClipEnabled(next);
    if (next) {
      adapter.setClippingBox(clipBoxFromCenterSize(clipCenter, clipSize));
    } else {
      adapter.setClippingBox(null);
    }
  }, [adapter, clipCenter, clipEnabled, clipSize]);

  const updateClipCenter = useCallback(
    (axis: 0 | 1 | 2, value: number) => {
      const next: [number, number, number] = [
        clipCenter[0],
        clipCenter[1],
        clipCenter[2],
      ];
      next[axis] = Math.max(0, Math.min(1, value));
      setClipCenter(next);
      if (clipEnabled) adapter?.setClippingBox?.(clipBoxFromCenterSize(next, clipSize));
    },
    [adapter, clipCenter, clipEnabled, clipSize],
  );

  const updateClipSize = useCallback(
    (value: number) => {
      const next = Math.max(0.05, Math.min(1, value));
      setClipSize(next);
      if (clipEnabled) adapter?.setClippingBox?.(clipBoxFromCenterSize(clipCenter, next));
    },
    [adapter, clipCenter, clipEnabled],
  );

  const applyRenderMode = useCallback(
    (mode: RenderMode) => {
      adapter?.setRenderMode?.(mode);
      setRenderModeState(mode);
    },
    [adapter],
  );

  return (
    <div
      role="toolbar"
      aria-label="Universal scene analysis tools"
      data-testid={testId ?? 'analysis-strip'}
      style={containerStyle}
    >
      <Stack direction="row" align="center" gap={2}>
        {/* View presets */}
        <div style={{ position: 'relative' }}>
          <IconButton
            aria-label="View presets"
            aria-pressed={presetsOpen}
            disabled={presetsDisabled || !adapter}
            onClick={() => setPresetsOpen((open) => !open)}
            data-testid="analysis-strip-views"
          >
            <Icon name="photo_camera" size={20} decorative />
          </IconButton>
          {presetsOpen ? (
            <div style={dropdownStyle} role="menu" aria-label="View presets">
              {CAMERA_PRESETS.map((entry) => (
                <button
                  type="button"
                  key={entry.preset}
                  role="menuitem"
                  onClick={() => applyPreset(entry.preset)}
                  disabled={presetsDisabled || !adapter}
                  style={menuItemStyle}
                  data-testid={`analysis-strip-view-${entry.preset}`}
                >
                  <Icon name={entry.icon} size={20} decorative />
                  <span>{entry.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <Divider />

        {/* Section plane */}
        <Stack direction="row" align="center" gap={1}>
          <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
            Section
          </Text>
          {SECTION_AXES.map((entry) => {
            const active = sectionAxis === entry.axis;
            return (
              <button
                type="button"
                key={entry.axis}
                aria-pressed={active}
                onClick={() => toggleSectionAxis(entry.axis)}
                disabled={!adapter?.setSectionPlane}
                data-testid={`analysis-strip-section-${entry.axis}`}
                style={axisButtonStyle(active)}
              >
                {entry.label}
              </button>
            );
          })}
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={sectionPosition}
            onChange={(event) =>
              updateSectionPosition(Number(event.currentTarget.value))
            }
            aria-label="Section plane offset"
            disabled={!sectionAxis}
            style={{ width: 96 }}
          />
        </Stack>

        <Divider />

        {/* Clipping box */}
        <IconButton
          aria-label="Clipping box"
          aria-pressed={clipEnabled}
          disabled={!adapter?.setClippingBox}
          onClick={toggleClipBox}
          data-testid="analysis-strip-clipbox"
          style={{
            background: clipEnabled ? tokens.color.surface[3] : 'transparent',
          }}
        >
          <Icon name="crop_free" size={20} decorative />
        </IconButton>
        {clipEnabled ? (
          <Stack direction="row" align="center" gap={1}>
            <Text variant="caption" as="span" style={{ color: tokens.color.ink.secondary }}>
              Box
            </Text>
            <AxisSlider
              label="Move clipping box on X axis"
              axis="X"
              value={clipCenter[0]}
              onChange={(value) => updateClipCenter(0, value)}
            />
            <AxisSlider
              label="Move clipping box on Y axis"
              axis="Y"
              value={clipCenter[1]}
              onChange={(value) => updateClipCenter(1, value)}
            />
            <AxisSlider
              label="Move clipping box on Z axis"
              axis="Z"
              value={clipCenter[2]}
              onChange={(value) => updateClipCenter(2, value)}
            />
            <AxisSlider
              label="Expand or shrink clipping box"
              axis="Size"
              value={clipSize}
              onChange={updateClipSize}
            />
          </Stack>
        ) : null}

        <Divider />

        {/* Render mode */}
        <Stack direction="row" align="center" gap={1}>
          {RENDER_MODES.map((entry) => {
            const active = renderMode === entry.mode;
            return (
              <button
                type="button"
                key={entry.mode}
                aria-pressed={active}
                onClick={() => applyRenderMode(entry.mode)}
                disabled={!adapter?.setRenderMode}
                title={entry.label}
                data-testid={`analysis-strip-render-${entry.mode}`}
                style={pillButtonStyle(active)}
              >
                <Icon name={entry.icon} size={16} decorative />
              </button>
            );
          })}
        </Stack>
      </Stack>
    </div>
  );
}

function Divider() {
  return (
    <span
      aria-hidden
      style={{
        width: tokens.border.width.thin,
        height: 20,
        background: tokens.color.surface[3],
      }}
    />
  );
}

function AxisSlider({
  axis,
  label,
  value,
  onChange,
}: {
  axis: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label style={axisSliderStyle}>
      <span style={axisLabelStyle}>{axis}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        aria-label={label}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
        style={{ width: axis === 'Size' ? 84 : 64 }}
      />
    </label>
  );
}

function clipBoxFromCenterSize(
  center: readonly [number, number, number],
  size: number,
): ClippingBoxSpec {
  const half = Math.max(0.025, Math.min(0.5, size / 2));
  const range = (axisCenter: number): readonly [number, number] => [
    Math.max(0, axisCenter - half),
    Math.min(1, axisCenter + half),
  ];
  const [xMin, xMax] = range(center[0]);
  const [yMin, yMax] = range(center[1]);
  const [zMin, zMax] = range(center[2]);
  return { xMin, xMax, yMin, yMax, zMin, zMax };
}

const containerStyle: CSSProperties = {
  position: 'absolute',
  top: tokens.space[2],
  left: tokens.space[2],
  zIndex: tokens.zIndex.dock,
  background: tokens.color.surface[1],
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  boxShadow: tokens.elevation[1],
  maxWidth: 'calc(100% - 2rem)',
};

const axisSliderStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.space[1],
};

const axisLabelStyle: CSSProperties = {
  color: tokens.color.ink.secondary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.xs,
  minWidth: 24,
};

const dropdownStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: tokens.space[1],
  display: 'flex',
  flexDirection: 'column',
  background: tokens.color.surface[1],
  border: tokens.border.thin,
  borderRadius: tokens.radius.md,
  boxShadow: tokens.elevation[2],
  minWidth: tokens.layout.popoverMd,
  zIndex: tokens.zIndex.dock,
};

const menuItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[2],
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  border: 0,
  background: 'transparent',
  color: tokens.color.ink.primary,
  cursor: 'pointer',
  textAlign: 'left',
  minHeight: tokens.density.touchMin,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
};

function axisButtonStyle(active: boolean): CSSProperties {
  return {
    minHeight: tokens.density.touchMin,
    minWidth: tokens.density.touchMin,
    paddingInline: tokens.space[1],
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: active
      ? tokens.color.accent.primary
      : tokens.color.surface[0],
    color: active ? tokens.color.accent.on : tokens.color.ink.primary,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
    fontWeight: active
      ? tokens.font.weight.medium
      : tokens.font.weight.regular,
    cursor: active ? 'default' : 'pointer',
  };
}

function pillButtonStyle(active: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: tokens.density.touchMin,
    minWidth: tokens.density.touchMin,
    paddingInline: tokens.space[1],
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: active
      ? tokens.color.accent.subtle
      : tokens.color.surface[0],
    color: tokens.color.ink.primary,
    cursor: 'pointer',
  };
}
